import React from 'react';
import { runtimeHooks } from './runtime-hooks.js';
import * as Recharts from 'recharts';
import { Icon } from './icons.jsx';
import { MACROS, REPORTS_BY_STATUS, REPORTS_TICKETS_DATA, REPORTS_TOP_TENANTS, SLA_RULES } from './support-data.jsx';
import { SupportTicketsPage } from './support-tickets-page.jsx';
// Suporte — Macros + SLA + Relatórios

// ── Tab Macros ────────────────────────────────────────────────────────────────
const MacrosTab = ({ onToast, onNewMacro }) => {
  const [macros,   setMacros]   = React.useState(MACROS || []);
  const [selected, setSelected] = React.useState(null);
  const [search,   setSearch]   = React.useState('');
  const [editing,  setEditing]  = React.useState(false);
  const [editBody, setEditBody] = React.useState('');

  // Expõe setter global para SupportTicketsPage injetar nova macro criada no modal
  React.useEffect(() => {
    runtimeHooks.macroTabAdd = (m) => setMacros(ms => [m, ...ms]);
    return () => { delete runtimeHooks.macroTabAdd; };
  }, []);

  React.useEffect(() => {
    if (!selected && macros.length) setSelected(macros[0]);
  }, [macros]);

  const filtered = macros.filter(m =>
    !search ||
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.category.toLowerCase().includes(search.toLowerCase())
  );

  const VARS = ['{{cliente_nome}}', '{{tenant_nome}}', '{{ticket_id}}', '{{link_artigo}}'];

  const openEdit = (m) => { setEditing(true); setEditBody(m.body); };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 400px', gap:16, alignItems:'start' }}>

      {/* ── Lista ── */}
      <div className="table-wrap">
        <div className="table-toolbar">
          <div className="search" style={{ maxWidth:280 }}>
            <Icon name="search" size={14}/>
            <input placeholder="Buscar macros…" value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <select className="select">
            <option value="">Categoria</option>
            {[...new Set(macros.map(m=>m.category))].map(c=><option key={c}>{c}</option>)}
          </select>
          <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
            <button className="btn sm" onClick={()=>onToast&&onToast('Nova pasta criada')}>
              <Icon name="plus" size={13}/>Nova pasta
            </button>
            <button className="btn sm primary" onClick={() => onNewMacro ? onNewMacro() : onToast&&onToast('Nova macro criada')}>
              <Icon name="plus" size={13}/>Nova macro
            </button>
          </div>
        </div>

        <table className="data">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Categoria</th>
              <th>Usada por</th>
              <th>Última edição</th>
              <th>Status</th>
              <th>Uso 30d</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => (
              <tr key={m.id}
                onClick={()=>{ setSelected(m); setEditing(false); }}
                style={{ cursor:'pointer', background: selected?.id===m.id ? 'color-mix(in srgb, var(--primary) 6%, var(--bg-elevated))' : undefined }}
              >
                <td>
                  <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                    <div style={{ width:28,height:28,borderRadius:6,background:'var(--info-bg)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                      <Icon name="zap" size={13} style={{ color:'var(--info)' }}/>
                    </div>
                    <span style={{ fontWeight:500, fontSize:13 }}>{m.name}</span>
                  </div>
                </td>
                <td><span className="badge neutral" style={{ fontSize:10 }}>{m.category}</span></td>
                <td style={{ fontSize:12,color:'var(--text-secondary)' }}>{m.usedBy} agentes</td>
                <td style={{ fontSize:12,color:'var(--text-muted)' }}>{m.lastEdit}</td>
                <td><span className="badge success" style={{ fontSize:10 }}><span className="dot"/>Ativo</span></td>
                <td>
                  <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                    <div style={{ width:48,height:4,borderRadius:2,background:'var(--bg-subtle)',overflow:'hidden' }}>
                      <div style={{ height:'100%',width:`${Math.min(100,m.uses30d/70*100)}%`,background:'var(--brand-navy)',borderRadius:2 }}/>
                    </div>
                    <span style={{ fontSize:12,fontWeight:600 }}>{m.uses30d}</span>
                  </div>
                </td>
                <td>
                  <div className="row-actions">
                    <button className="icon-action" title="Editar" onClick={e=>{e.stopPropagation();openEdit(m);}}>
                      <Icon name="edit" size={13}/>
                    </button>
                    <button className="icon-action danger" title="Excluir" onClick={e=>{e.stopPropagation();onToast&&onToast('Macro excluída');}}>
                      <Icon name="trash" size={13}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Preview / Editor ── */}
      {selected && (
        <div className="card" style={{ overflow:'hidden' }}>
          <div style={{ padding:'13px 16px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:9 }}>
            <div style={{ width:32,height:32,borderRadius:8,background:'var(--info-bg)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
              <Icon name="zap" size={15} style={{ color:'var(--info)' }}/>
            </div>
            <div style={{ flex:1,minWidth:0 }}>
              <div style={{ fontWeight:600,fontSize:14 }}>{selected.name}</div>
              <div style={{ fontSize:11,color:'var(--text-muted)' }}>{selected.category} · {selected.uses30d} usos em 30 dias</div>
            </div>
            <button className="btn sm primary" onClick={()=>onToast&&onToast('Macro aplicada ao ticket')}>
              <Icon name="send" size={12}/>Aplicar
            </button>
          </div>

          {editing ? (
            <div style={{ padding:'14px 16px' }}>
              <div style={{ fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginBottom:8 }}>Editar conteúdo</div>
              <textarea
                value={editBody}
                onChange={e=>setEditBody(e.target.value)}
                style={{ width:'100%',height:200,padding:'10px 12px',borderRadius:8,border:'1px solid var(--border-strong)',background:'var(--bg-elevated)',color:'var(--text)',fontSize:13,fontFamily:'inherit',resize:'vertical',outline:'none',lineHeight:1.6 }}
              />
              <div style={{ display:'flex',gap:8,marginTop:10 }}>
                <button className="btn sm primary" onClick={()=>{setEditing(false);onToast&&onToast('Macro salva');}}>
                  <Icon name="check" size={12}/>Salvar
                </button>
                <button className="btn sm ghost" onClick={()=>setEditing(false)}>Cancelar</button>
              </div>
            </div>
          ) : (
            <div style={{ padding:'13px 16px',borderBottom:'1px solid var(--border)' }}>
              <div style={{ fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text-muted)',marginBottom:8 }}>Pré-visualização</div>
              <div style={{ background:'var(--bg-subtle)',borderRadius:8,padding:'12px 14px',fontSize:13,lineHeight:1.6,color:'var(--text-secondary)',whiteSpace:'pre-wrap' }}>
                {selected.body}
              </div>
              <button className="btn sm ghost" style={{ marginTop:10 }} onClick={()=>openEdit(selected)}>
                <Icon name="edit" size={12}/>Editar conteúdo
              </button>
            </div>
          )}

          <div style={{ padding:'12px 16px' }}>
            <div style={{ fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text-muted)',marginBottom:8 }}>Variáveis disponíveis</div>
            <div style={{ display:'flex',flexWrap:'wrap',gap:6 }}>
              {VARS.map(v=>(
                <code key={v} style={{ fontSize:11,background:'var(--info-bg)',color:'var(--info)',padding:'2px 8px',borderRadius:6,border:'1px solid color-mix(in srgb, var(--info) 25%, transparent)',fontFamily:'JetBrains Mono, monospace' }}>
                  {v}
                </code>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tab SLA ───────────────────────────────────────────────────────────────────
const SLATab = ({ onToast }) => {
  const rules = SLA_RULES || [];

  const PRIO_CFG = {
    baixa:   { label:'Baixa',   cls:'neutral' },
    media:   { label:'Média',   cls:'info'    },
    alta:    { label:'Alta',    cls:'warning' },
    urgente: { label:'Urgente', cls:'danger'  },
  };

  const slaKpis = [
    { label:'SLA vencido hoje',       value: 2,         icon:'alert',          danger:true },
    { label:'Vencendo em 1h',         value: 3,         icon:'clock',          warn:true   },
    { label:'1ª resposta média',      value: '2h 14min',icon:'messageCircle'              },
    { label:'Cumprimento de SLA',     value: '94.2%',   icon:'check',          ok:true     },
  ];

  const riskTickets = [
    { id:'#4819', subject:'Erro ao integrar com Bling',           tenant:'Tech Sul',          sla:'Vencido há 1h',    priority:'urgente' },
    { id:'#4821', subject:'Não consigo configurar meu domínio',   tenant:'Loja Aurora',       sla:'2h restantes',     priority:'alta'    },
    { id:'#4818', subject:'Cliente não recebeu e-mail transacional',tenant:'Moda Viva',       sla:'4h restantes',     priority:'alta'    },
    { id:'#4812', subject:'Relatório de vendas com dados errados', tenant:'Mercado Alto Giro', sla:'6h restantes',     priority:'media'   },
  ];

  const businessHours = [
    { day:'Segunda a Sexta', hours:'08:00 – 18:00' },
    { day:'Sábado',          hours:'09:00 – 13:00' },
    { day:'Domingo',         hours:'Fechado'        },
    { day:'Feriados',        hours:'Fechado'        },
  ];

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:20 }}>
      {/* KPIs */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12 }}>
        {slaKpis.map(k=>(
          <div key={k.label} className="kpi">
            <div className="label"><Icon name={k.icon} size={14}/>{k.label}</div>
            <div className="value" style={{ fontSize:k.value.toString().length>5?18:26, color:k.danger?'var(--danger)':k.warn?'var(--warning)':k.ok?'var(--success)':undefined }}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid',gridTemplateColumns:'1fr 300px',gap:16,alignItems:'start' }}>
        <div style={{ display:'flex',flexDirection:'column',gap:16 }}>

          {/* Rules table */}
          <div className="table-wrap">
            <div className="table-toolbar">
              <div style={{ fontWeight:600,fontSize:14 }}>Regras de SLA</div>
              <span style={{ fontSize:12,color:'var(--text-muted)',marginLeft:6 }}>{rules.length} regras</span>
              <div style={{ marginLeft:'auto' }}>
                <button className="btn sm primary" onClick={()=>onToast&&onToast('Nova regra de SLA criada')}>
                  <Icon name="plus" size={13}/>Nova regra
                </button>
              </div>
            </div>
            <table className="data">
              <thead>
                <tr>
                  <th>Nome da regra</th>
                  <th>Prioridade</th>
                  <th>Plano</th>
                  <th>1ª resposta</th>
                  <th>Resolução</th>
                  <th>Escalonamento</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rules.map(r=>{
                  const pc = PRIO_CFG[r.priority]||PRIO_CFG.media;
                  return (
                    <tr key={r.id}>
                      <td style={{ fontWeight:500,fontSize:13 }}>{r.name}</td>
                      <td><span className={`badge ${pc.cls}`} style={{ fontSize:10 }}><span className="dot"/>{pc.label}</span></td>
                      <td><span className="badge neutral" style={{ fontSize:10 }}>{r.plan}</span></td>
                      <td style={{ fontWeight:600,fontSize:13,color:'var(--text)' }}>{r.firstResponse}</td>
                      <td style={{ fontWeight:600,fontSize:13,color:'var(--text)' }}>{r.resolution}</td>
                      <td style={{ fontSize:12,color:r.escalation==='—'?'var(--text-muted)':'var(--text)' }}>{r.escalation}</td>
                      <td><span className="badge success" style={{ fontSize:10 }}><span className="dot"/>Ativo</span></td>
                      <td>
                        <div className="row-actions">
                          <button className="icon-action" onClick={()=>onToast&&onToast('Editando regra…')}><Icon name="edit" size={13}/></button>
                          <button className="icon-action danger" onClick={()=>onToast&&onToast('Regra excluída')}><Icon name="trash" size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Tickets at risk */}
          <div className="table-wrap">
            <div style={{ padding:'12px 16px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:8 }}>
              <Icon name="alert" size={14} style={{ color:'var(--warning)' }}/>
              <div style={{ fontWeight:600,fontSize:14 }}>Tickets em risco de SLA</div>
              <span className="badge warning" style={{ fontSize:10,marginLeft:4 }}><span className="dot"/>{riskTickets.length}</span>
            </div>
            <table className="data">
              <thead>
                <tr><th>Ticket</th><th>Tenant</th><th>SLA</th><th>Prioridade</th><th></th></tr>
              </thead>
              <tbody>
                {riskTickets.map(t=>{
                  const pc = PRIO_CFG[t.priority]||PRIO_CFG.media;
                  const expired = t.sla.includes('Vencido');
                  return (
                    <tr key={t.id}>
                      <td>
                        <div style={{ fontWeight:500,fontSize:13 }}>{t.subject}</div>
                        <span className="mono" style={{ fontSize:11,color:'var(--text-muted)' }}>{t.id}</span>
                      </td>
                      <td style={{ fontSize:13 }}>{t.tenant}</td>
                      <td>
                        <span style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:20,background:expired?'var(--danger-bg)':'var(--warning-bg)',color:expired?'var(--danger)':'var(--warning)',fontSize:11,fontWeight:600 }}>
                          <Icon name={expired?'alert':'clock'} size={11}/>{t.sla}
                        </span>
                      </td>
                      <td><span className={`badge ${pc.cls}`} style={{ fontSize:10 }}><span className="dot"/>{pc.label}</span></td>
                      <td>
                        <div className="row-actions">
                          <button className="icon-action" title="Abrir ticket" onClick={()=>onToast&&onToast('Abrindo ticket…')}><Icon name="externalLink" size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: business hours */}
        <div className="card" style={{ overflow:'hidden' }}>
          <div style={{ padding:'11px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:7 }}>
            <Icon name="clock" size={13} style={{ color:'var(--text-muted)' }}/>
            <div style={{ fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text-muted)' }}>Horário de atendimento</div>
          </div>
          <div style={{ padding:'12px 14px' }}>
            {businessHours.map(h=>(
              <div key={h.day} style={{ display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border)',fontSize:13 }}>
                <span style={{ color:'var(--text-secondary)' }}>{h.day}</span>
                <span style={{ fontWeight:600,color:h.hours==='Fechado'?'var(--text-muted)':'var(--text)' }}>{h.hours}</span>
              </div>
            ))}
            <div style={{ marginTop:12,padding:'10px 12px',background:'var(--bg-subtle)',borderRadius:8,fontSize:12 }}>
              <div style={{ fontWeight:600,marginBottom:3,color:'var(--text)' }}>Fuso horário</div>
              <div style={{ color:'var(--text-secondary)' }}>America/Sao_Paulo (UTC-3)</div>
            </div>
            <button className="btn sm" style={{ width:'100%',marginTop:10,justifyContent:'center' }} onClick={()=>onToast&&onToast('Editando horários…')}>
              <Icon name="edit" size={13}/>Editar horários
            </button>
          </div>

          {/* Escalation */}
          <div style={{ padding:'12px 14px',borderTop:'1px solid var(--border)' }}>
            <div style={{ fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--text-muted)',marginBottom:10 }}>Escalonamentos ativos</div>
            {[
              { rule:'Pro — Urgente', trigger:'Sem resposta em 1h',   to:'Gerente de Suporte' },
              { rule:'Enterprise',   trigger:'Sem resposta em 30min', to:'Rafael Tavares'     },
            ].map((e,i)=>(
              <div key={i} style={{ padding:'8px 10px',background:'var(--bg-subtle)',borderRadius:8,marginBottom:6,fontSize:12 }}>
                <div style={{ fontWeight:600,color:'var(--text)',marginBottom:2 }}>{e.rule}</div>
                <div style={{ color:'var(--text-muted)' }}>Gatilho: {e.trigger}</div>
                <div style={{ color:'var(--text-secondary)' }}>Notifica: {e.to}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Tab Relatórios ────────────────────────────────────────────────────────────
const ReportsTab = ({ onToast }) => {
  const { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } = Recharts;

  const ticketsData  = REPORTS_TICKETS_DATA  || [];
  const statusData   = REPORTS_BY_STATUS     || [];
  const topTenants   = REPORTS_TOP_TENANTS   || [];

  const [period, setPeriod] = React.useState('30d');

  const reportKpis = [
    { label:'Tickets criados',    value: 168,       delta:'+12%',   up:true,  icon:'plus'          },
    { label:'Resolvidos',         value: 146,       delta:'+8%',    up:true,  icon:'check'         },
    { label:'1ª resposta média',  value: '2h 14min',delta:'-18min', up:true,  icon:'clock'         },
    { label:'Tempo de resolução', value: '16h 42min',delta:'-2h',   up:true,  icon:'activity'      },
    { label:'CSAT',               value: '4.7/5',   delta:'+0.2',   up:true,  icon:'star'          },
    { label:'Resol. por artigo',  value: '32%',     delta:'+4%',    up:true,  icon:'bookOpen'      },
  ];

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:20 }}>
      {/* Header row */}
      <div style={{ display:'flex',alignItems:'center',gap:10 }}>
        <div style={{ flex:1,fontWeight:600,fontSize:15 }}>Relatório de Suporte</div>
        <div className="filter-chips">
          {['7d','30d','90d'].map(p=>(
            <button key={p} className={'filter-chip'+(period===p?' active':'')} onClick={()=>setPeriod(p)}>
              Últimos {p==='7d'?'7 dias':p==='30d'?'30 dias':'90 dias'}
            </button>
          ))}
        </div>
        <button className="btn sm ghost" onClick={()=>onToast&&onToast('Relatório exportado')}>
          <Icon name="download" size={13}/>Exportar
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:12 }}>
        {reportKpis.map(k=>(
          <div key={k.label} className="kpi">
            <div className="label"><Icon name={k.icon} size={14}/>{k.label}</div>
            <div className="value" style={{ fontSize:k.value.toString().length>7?16:k.value.toString().length>5?20:26 }}>{k.value}</div>
            {k.delta && (
              <div className={`delta ${k.up?'up':'down'}`}>
                <Icon name={k.up?'arrowUp':'arrowDown'} size={11}/>
                {k.delta} vs anterior
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style={{ display:'grid',gridTemplateColumns:'1fr 300px',gap:16 }}>
        <div className="card">
          <div className="card-head">
            <h3>Volume de tickets por dia</h3>
            <span className="sub">últimos 11 dias</span>
            <div className="actions">
              <button className="btn sm ghost" onClick={()=>onToast&&onToast('Exportando gráfico…')}>
                <Icon name="download" size={13}/>
              </button>
            </div>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={190}>
              <LineChart data={ticketsData} margin={{ top:4,right:8,bottom:0,left:-20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                <XAxis dataKey="dia" tick={{ fontSize:11,fill:'var(--text-muted)' }}/>
                <YAxis tick={{ fontSize:11,fill:'var(--text-muted)' }}/>
                <Tooltip contentStyle={{ background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,fontSize:12 }}/>
                <Legend wrapperStyle={{ fontSize:12 }}/>
                <Line type="monotone" dataKey="criados"   stroke="#1B3D60" strokeWidth={2} dot={{ r:3 }} name="Criados"/>
                <Line type="monotone" dataKey="resolvidos" stroke="#0f7a3e" strokeWidth={2} dot={{ r:3 }} name="Resolvidos"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>Por status</h3></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={38} outerRadius={66} dataKey="value" paddingAngle={2}>
                  {statusData.map((e,i)=><Cell key={i} fill={e.color}/>)}
                </Pie>
                <Tooltip contentStyle={{ background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,fontSize:12 }}/>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display:'flex',flexDirection:'column',gap:4 }}>
              {statusData.map((s,i)=>(
                <div key={i} style={{ display:'flex',alignItems:'center',gap:8,fontSize:12 }}>
                  <div style={{ width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0 }}/>
                  <span style={{ flex:1,color:'var(--text-secondary)' }}>{s.name}</span>
                  <span style={{ fontWeight:600 }}>{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Top tenants */}
      <div className="table-wrap">
        <div className="table-toolbar">
          <div style={{ fontWeight:600,fontSize:14 }}>Ranking por volume de suporte</div>
          <span style={{ fontSize:12,color:'var(--text-muted)',marginLeft:8 }}>Maio 2026</span>
          <div style={{ marginLeft:'auto' }}>
            <button className="btn sm ghost" onClick={()=>onToast&&onToast('CSV exportado')}>
              <Icon name="download" size={13}/>Exportar CSV
            </button>
          </div>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Plano</th>
              <th>Abertos</th>
              <th>Resolvidos</th>
              <th>SLA vencido</th>
              <th>Categoria principal</th>
            </tr>
          </thead>
          <tbody>
            {topTenants.map((t,i)=>(
              <tr key={i}>
                <td>
                  <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                    <span style={{ fontSize:11,fontWeight:700,color:'var(--text-muted)',width:16,flexShrink:0 }}>{i+1}.</span>
                    <span style={{ fontWeight:500 }}>{t.tenant}</span>
                  </div>
                </td>
                <td><span className="badge neutral" style={{ fontSize:10 }}>{t.plan}</span></td>
                <td style={{ fontWeight:600,color:t.open>3?'var(--warning)':'var(--text)' }}>{t.open}</td>
                <td style={{ color:'var(--success)',fontWeight:500 }}>{t.resolved}</td>
                <td>
                  {t.slaBreaches>0
                    ? <span className="badge danger" style={{ fontSize:10 }}><span className="dot"/>{t.slaBreaches} vencimento{t.slaBreaches>1?'s':''}</span>
                    : <span style={{ fontSize:12,color:'var(--text-muted)' }}>—</span>}
                </td>
                <td>
                  <span style={{ background:'var(--bg-subtle)',color:'var(--text-secondary)',fontSize:11,padding:'2px 8px',borderRadius:20,border:'1px solid var(--border)',fontWeight:500 }}>{t.topCategory}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export {
  MacrosTab,
  ReportsTab,
  SLATab,
};
